import { format, isBefore, parseISO } from 'date-fns'
import Image from 'next/image'
import React from 'react'

import { Response_WorkDetailEpisode } from '@/api/@types'
import { Tag } from '@/components/common/DataDisplay'

import { IconCoin, IconPlayArrow } from '@/components/common/General'

const THUMBNAIL_WIDTH_AT_PORTRAIT = 78
const THUMBNAIL_HEIGHT_AT_PORTRAIT = 139

const THUMBNAIL_WIDTH_AT_LANDSCAPE = 139
const THUMBNAIL_HEIGHT_AT_LANDSCAPE = 78

const BREAK_POINT = 640
const EPISODE_SPACING = 24

export type EpisodeType = Response_WorkDetailEpisode

type EpisodeProps = React.ComponentProps<'button'> & EpisodeType

export const Episode = ({
  thumbnailUrl,
  number,
  name,
  videoLength,
  isFree,
  isPortrait,
  isWaitFree,
  isCommercialFree,
  releaseAt,
  onClick,
}: EpisodeProps) => {
  const deviceWidth = window.innerWidth
  const textMaxWidthAtPortrait =
    deviceWidth > BREAK_POINT
      ? 273
      : deviceWidth - THUMBNAIL_WIDTH_AT_PORTRAIT - EPISODE_SPACING
  const textMaxWidthAtLandscape =
    deviceWidth > BREAK_POINT
      ? 212
      : deviceWidth - THUMBNAIL_WIDTH_AT_LANDSCAPE - EPISODE_SPACING

  const utcReleaseDate = React.useMemo(() => {
    if (!releaseAt) {
      return new Date()
    }

    return parseISO(releaseAt)
  }, [releaseAt])

  const isBeforeDeliveryDate = React.useMemo(() => {
    return isBefore(new Date(), utcReleaseDate)
  }, [utcReleaseDate])

  const formatDate = React.useMemo(() => {
    if (!isBeforeDeliveryDate) {
      return null
    }

    return format(utcReleaseDate, 'yyyy.MM.dd HH:mm')
  }, [utcReleaseDate, isBeforeDeliveryDate])

  const optionIcons = React.useMemo(() => {
    const icons = []
    if (isBeforeDeliveryDate) {
      return []
    }

    if (isFree) {
      icons.push(<Tag variantColor="free">無料</Tag>)
    }

    if (isWaitFree) {
      icons.push(<Tag variantColor="waitFree">待つと無料</Tag>)
    }
    if (isCommercialFree) {
      icons.push(<Tag variantColor="adFree">CMで無料</Tag>)
    }

    if (!isFree) {
      icons.push(<IconCoin width={32} height={32} />)
    }

    return icons
  }, [isBeforeDeliveryDate, isFree, isWaitFree, isCommercialFree])

  const timeFormat = (timeSec: number) => {
    timeSec = timeSec || 0
    const mm = ('00' + ~~(~~(timeSec / 60) % 60)).slice(-2)
    const ss = ('00' + ~~(timeSec % 60)).slice(-2)
    return mm + ':' + ss
  }

  return (
    <div className="relative z-10">
      <button
        className="flex justify-between items-center w-full"
        disabled={!onClick}
        onClick={onClick}
      >
        <div className="flex">
          <div
            className="flex relative"
            style={{
              width: isPortrait
                ? `${THUMBNAIL_WIDTH_AT_PORTRAIT}px`
                : `${THUMBNAIL_WIDTH_AT_LANDSCAPE}px`,
              height: isPortrait
                ? `${THUMBNAIL_HEIGHT_AT_PORTRAIT}px`
                : `${THUMBNAIL_HEIGHT_AT_LANDSCAPE}px`,
            }}
          >
            <Image
              src={thumbnailUrl ?? ''}
              alt={name}
              width={
                isPortrait
                  ? THUMBNAIL_WIDTH_AT_PORTRAIT
                  : THUMBNAIL_WIDTH_AT_LANDSCAPE
              }
              height={
                isPortrait
                  ? THUMBNAIL_HEIGHT_AT_PORTRAIT
                  : THUMBNAIL_HEIGHT_AT_LANDSCAPE
              }
              className="rounded"
            />

            <div className="flex absolute inset-0 justify-center items-center">
              <IconPlayArrow className="text-2xl" />
            </div>
          </div>
          <div className="flex flex-col items-start ml-8 space-y-4">
            <p
              className="overflow-hidden font-bold text-left overflow-ellipsis whitespace-nowrap"
              style={{
                width: isPortrait
                  ? `${textMaxWidthAtPortrait}px`
                  : `${textMaxWidthAtLandscape}px`,
              }}
            >
              #{number} {name}
            </p>
            <p className="italic font-bold text-secondary">
              {isBeforeDeliveryDate
                ? `${formatDate} 配信予定`
                : timeFormat(videoLength || 0)}
            </p>
            <div className="flex items-center">{optionIcons.map(e => e)}</div>
          </div>
        </div>
      </button>
    </div>
  )
}
